import * as THREE from "three";

const SEGMENTS = 48;

/** World-space ribbon that follows hull velocity instead of point sprites. */
export class BoostTrail {
  private readonly mesh: THREE.Mesh;
  private readonly positions: Float32Array;
  private readonly history: Array<{ x: number; y: number; z: number }> = [];
  private lastColor = -1;

  public constructor(scene: THREE.Scene) {
    this.positions = new Float32Array(SEGMENTS * 2 * 3);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(this.positions, 3));
    const index: number[] = [];
    for (let i = 0; i < SEGMENTS - 1; i += 1) {
      const a = i * 2;
      index.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
    geo.setIndex(index);
    this.mesh = new THREE.Mesh(
      geo,
      new THREE.MeshBasicMaterial({
        color: 0xc45c26,
        transparent: true,
        opacity: 0.72,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
        toneMapped: false,
      }),
    );
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);
  }

  public sync(
    x: number,
    z: number,
    altitude: number,
    vx: number,
    vz: number,
    color: number,
    speed: number,
  ): void {
    this.history.unshift({ x, y: altitude, z });
    if (this.history.length > SEGMENTS) this.history.pop();

    const mat = this.mesh.material as THREE.MeshBasicMaterial;
    if (color !== this.lastColor) {
      mat.color.setHex(color);
      this.lastColor = color;
    }
    const boosting = speed > 46;
    mat.opacity = boosting ? 0.92 : 0.55;
    const half = boosting ? 0.55 : 0.28;

    let dx = vx;
    let dz = vz;
    const mag = Math.hypot(dx, dz);
    if (mag < 0.4) {
      dx = 1;
      dz = 0;
    } else {
      dx /= mag;
      dz /= mag;
    }
    const px = -dz;
    const pz = dx;

    for (let i = 0; i < SEGMENTS; i += 1) {
      const sample = this.history[Math.min(i, this.history.length - 1)] ?? { x, y: altitude, z };
      const w = half * (1 - i / SEGMENTS);
      const a = i * 6;
      this.positions[a] = sample.x + px * w;
      this.positions[a + 1] = sample.y;
      this.positions[a + 2] = sample.z + pz * w;
      this.positions[a + 3] = sample.x - px * w;
      this.positions[a + 4] = sample.y;
      this.positions[a + 5] = sample.z - pz * w;
    }
    const attr = this.mesh.geometry.attributes.position;
    if (attr) attr.needsUpdate = true;
  }

  public dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
    this.mesh.removeFromParent();
  }
}
